import React from 'react';
import {
  FaBrain,
  FaChartLine,
  FaClipboardCheck,
  FaHeartbeat,
  FaHospitalUser,
  FaLock,
  FaNotesMedical,
  FaRobot,
  FaStethoscope,
  FaUserMd,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar';
import BottomContent from '../../components/BottomContent';
import ScrollButton from "../../components/ScrollButton";
import ContactButton from '../../components/ContactButton';
import Thank from '../../components/Thank';
import DarkModeButton from '../../components/DarkModeButton';
import HealthcareImg from '../../assets/img/HealthcareHero.avif';
import AIAutomationImg from '../../assets/ServicesImg/AI-Automation.webp';
import DataScienceImg from '../../assets/ServicesImg/Data-Science.webp';
import CyberSecurityImg from '../../assets/ServicesImg/Cyber-Security.webp';
import '../CustomStyle.css';
import './HealthCare.css';

const impactStats = [
  { value: '24/7', label: 'Intelligent monitoring' },
  { value: 'AI', label: 'Clinical decision support' },
  { value: '360°', label: 'Connected patient workflows' },
];

const capabilityCards = [
  {
    icon: <FaStethoscope />,
    title: 'AI Diagnostics',
    text: 'Enhancing disease detection and medical imaging analysis with machine learning.',
  },
  {
    icon: <FaChartLine />,
    title: 'Predictive Analytics',
    text: 'Forecasting patient outcomes and identifying potential health risks through AI models.',
  },
  {
    icon: <FaRobot />,
    title: 'Automated Healthcare',
    text: 'Streamlining administrative tasks to improve hospital efficiency and patient experience.',
  },
];

const visualHighlights = [
  {
    image: AIAutomationImg,
    title: 'Intelligent workflow automation',
    text: 'Reduce manual handoffs across scheduling, reporting, and care coordination.',
  },
  {
    image: DataScienceImg,
    title: 'Clinical data intelligence',
    text: 'Turn patient, operational, and diagnostic data into faster decisions.',
  },
  {
    image: CyberSecurityImg,
    title: 'Secure healthcare systems',
    text: 'Support privacy-conscious healthcare platforms with stronger digital safeguards.',
  },
];

const benefits = [
  { title: 'Enhanced Accuracy', text: 'AI improves diagnostics and treatment plans by reducing human error.' },
  { title: 'Faster Processing', text: 'AI-powered systems speed up patient diagnoses and administrative workflows.' },
  { title: 'Cost Efficiency', text: 'Reducing manual effort and optimizing hospital resources through AI automation.' },
  { title: 'Improved Patient Outcomes', text: 'AI-driven insights support personalized treatment strategies for better healthcare results.' },
];

const solutions = [
  {
    icon: <FaHeartbeat />,
    title: 'AI-Driven Patient Monitoring',
    text: 'Continuous monitoring systems that detect anomalies and provide real-time alerts.',
  },
  {
    icon: <FaHospitalUser />,
    title: 'AI-Powered Virtual Assistants',
    text: 'Chatbots and voice assistants for scheduling, medication reminders, and patient queries.',
  },
  {
    icon: <FaBrain />,
    title: 'Automated Medical Imaging',
    text: 'Advanced AI tools to assist in MRI, CT scans, and X-ray analysis for early disease detection.',
  },
  {
    icon: <FaNotesMedical />,
    title: 'Smart Health Records',
    text: 'AI-powered electronic health records that optimize patient data management and accessibility.',
  },
];

const challenges = [
  'Data Privacy: Ensuring secure patient data storage and compliance with healthcare regulations.',
  'Integration Barriers: Implementing AI solutions seamlessly into existing hospital infrastructure.',
  'Trust and Training: Educating healthcare professionals on AI benefits and overcoming skepticism in AI-assisted care.',
];

export default function HealthCare() {
  return (
    <>
      <Navbar />

      <main className="healthcare-page">
        <section className="healthcare-hero">
          <div className="healthcare-hero-bg" style={{ backgroundImage: `url(${HealthcareImg})` }}></div>
          <div className="healthcare-hero-overlay"></div>
          <div className="container healthcare-hero-inner">
            <div className="healthcare-hero-copy">
              <span className="healthcare-eyebrow">Healthcare AI</span>
              <h1>AI-Powered Healthcare Solutions</h1>
              <h2>Transforming healthcare with AI for better patient care and efficiency.</h2>
              <p>
                Empowering healthcare with cutting-edge AI solutions to enhance diagnostics,
                streamline operations, and improve patient outcomes. Our technology-driven
                approach transforms medical processes, ensuring accuracy, efficiency, and
                better care for all.
              </p>
              <div className="healthcare-actions">
                <a href="#healthcare-solutions" className="healthcare-btn healthcare-btn-primary">
                  Explore solutions
                </a>
                <a href="#healthcare-benefits" className="healthcare-btn healthcare-btn-secondary">
                  View benefits
                </a>
              </div>
            </div>

            <aside className="healthcare-command-panel" aria-label="Healthcare AI impact">
              <div className="healthcare-command-header">
                <FaUserMd />
                <span>Care intelligence layer</span>
              </div>
              <div className="healthcare-pulse-card">
                <strong>Better decisions</strong>
                <p>Diagnostics, operations, and patient engagement connected through AI.</p>
              </div>
              <div className="healthcare-stat-grid">
                {impactStats.map((item) => (
                  <div key={item.label}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section className="healthcare-section healthcare-intro-section">
          <div className="container">
            <div className="healthcare-split">
              <div>
                <span className="healthcare-eyebrow">Transforming care delivery</span>
                <h2>Transforming Healthcare with AI Solutions</h2>
              </div>
              <p>
                At Vedalytics, we integrate AI-driven innovations into healthcare,
                enhancing patient outcomes, improving diagnosis accuracy, and optimizing
                hospital operations. Our solutions empower healthcare providers with
                intelligent, data-driven decision-making capabilities.
              </p>
            </div>
          </div>
        </section>

        <section className="healthcare-visual-section" aria-label="Healthcare AI visual workflow">
          <div className="container">
            <div className="healthcare-visual-grid">
              {visualHighlights.map((item) => (
                <article className="healthcare-visual-card" key={item.title}>
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="healthcare-section healthcare-capabilities-section">
          <div className="container">
            <div className="healthcare-section-heading">
              <span className="healthcare-eyebrow">AI-driven healthcare solutions</span>
              <h2>Smarter systems for modern providers.</h2>
              <p>
                Our AI solutions redefine healthcare by enabling predictive diagnostics,
                streamlining workflows, and automating processes.
              </p>
            </div>

            <div className="healthcare-capability-grid">
              {capabilityCards.map((card) => (
                <article className="healthcare-capability-card" key={card.title}>
                  <div className="healthcare-card-icon">{card.icon}</div>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="healthcare-section healthcare-benefits-section" id="healthcare-benefits">
          <div className="container">
            <div className="healthcare-section-heading">
              <span className="healthcare-eyebrow">Benefits of AI in healthcare</span>
              <h2>Designed to improve accuracy, speed, and outcomes.</h2>
            </div>

            <div className="healthcare-benefit-grid">
              {benefits.map((benefit, index) => (
                <article className="healthcare-benefit-card" key={benefit.title}>
                  <small>{String(index + 1).padStart(2, '0')}</small>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="healthcare-section healthcare-solutions-section" id="healthcare-solutions">
          <div className="container">
            <div className="healthcare-section-heading">
              <span className="healthcare-eyebrow">Tailored provider solutions</span>
              <h2>AI solutions built around clinical and operational workflows.</h2>
            </div>

            <div className="healthcare-solution-grid">
              {solutions.map((solution) => (
                <article className="healthcare-solution-card" key={solution.title}>
                  <div className="healthcare-card-icon">{solution.icon}</div>
                  <div>
                    <h3>{solution.title}</h3>
                    <p>{solution.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="healthcare-section healthcare-risk-section">
          <div className="container">
            <div className="healthcare-risk-panel">
              <div>
                <span className="healthcare-eyebrow">Responsible adoption</span>
                <h2>Challenges in AI Adoption for Healthcare</h2>
              </div>
              <div className="healthcare-challenge-list">
                {challenges.map((challenge) => (
                  <div className="healthcare-challenge-item" key={challenge}>
                    <FaLock />
                    <span>{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="healthcare-section healthcare-future-section">
          <div className="container">
            <div className="healthcare-future-panel">
              <div className="healthcare-card-icon">
                <FaClipboardCheck />
              </div>
              <span className="healthcare-eyebrow">The future of AI in healthcare</span>
              <h2>Building intelligent healthcare ecosystems.</h2>
              <p>
                AI is shaping the future of healthcare by enabling precision medicine,
                robotic surgeries, and AI-assisted diagnostics. Our commitment lies in
                developing intelligent healthcare ecosystems that drive efficiency,
                accuracy, and better patient care outcomes.
              </p>
              <a href="/contact" className="healthcare-btn healthcare-btn-primary">
                Start a healthcare AI conversation
              </a>
            </div>
          </div>
        </section>
      </main>

      <Thank />
      <BottomContent />
      <DarkModeButton />
      <ContactButton />
      <ScrollButton />
    </>
  );
}
